import React, { useState, useEffect } from "react";
import { Box, Layer, Text, Button, Spinner } from "grommet"; // Import Spinner
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import { HeaderNavigation } from "../../components/Headers/GlobalHeader";
import { QuizHeader } from "../../components/QuizComponents/QuizHeader";
import { QuizContent } from "../../components/QuizComponents/QuizContent";
import axios from "axios"; // Import axios for API calls
import API_URL from "../../config/config";
import { useQuizContext } from "../../contexts/QuizContext";

export const ManagerQuizPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user_id, assessment_id } = useParams();

  const [questions, setQuestions] = useState([{}]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [showSubmitPopup, setShowSubmitPopup] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [numberOfQuestions, setNumberOfQuestions] = useState(null); // Initialize as null
  const [noAnswerQuestionIds, setNoAnswerQuestionIds] = useState([]); // New state to track "No" answers
  const [loading, setLoading] = useState(true); // Loading state for showing the loading screen
  const [submitLoading, setSubmitLoading] = useState(false);
  const { resetQuizAttempt, allowQuizAttempt } = useQuizContext();
  const subProductSkillIds = location.state?.subProductSkillIds || [];
  const [query_count, setQueryCount] = useState(null); // Initialize as null

  // Function to format the date as YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = `0${date.getMonth() + 1}`.slice(-2);
    const day = `0${date.getDate()}`.slice(-2);
    return `${year}-${month}-${day}`;
  };

  // Function to format the time as HH:MM:SS
  const formatStartTime = (date) => {
    const hours = `0${date.getHours()}`.slice(-2);
    const minutes = `0${date.getMinutes()}`.slice(-2);
    const seconds = `0${date.getSeconds()}`.slice(-2);
    return `${hours}:${minutes}:${seconds}`;
  };

  // Fetch questions from the backend
  const fetchQuestions = async () => {
    try {
      
      setLoading(true); // Start loading
      
      const response = await axios.post(
        `${API_URL}/manager/${user_id}/questions/${assessment_id}`,
        { subProductSkillIds },
      );
      if (response) {
        const fetchedQuestions = response.data.selectedQuestions;
        
        setQuestions(fetchedQuestions);
        setQueryCount(fetchedQuestions.length); // Set query_count here based on fetched questions
        localStorage.setItem("currentAssessmentId", assessment_id);
      } else {
        alert("Failed to fetch questions from the server.");
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setLoading(false); // Stop loading after fetching questions
    }
  };

  // Handle actions after query_count is set
  useEffect(() => {
    if (query_count !== null) {
      const savedProgress =
        JSON.parse(localStorage.getItem("localQuizProgress")) || {};
      const storedAssessmentId = localStorage.getItem("currentAssessmentId");

      // Check if stored assessment_id matches the current assessment_id
      if (storedAssessmentId === assessment_id) {
        const initialNumberOfQuestions =
          savedProgress.numberOfQuestions || query_count;

        setNumberOfQuestions(initialNumberOfQuestions);

        // Restore saved progress if any
        setCurrentQuestionIndex(savedProgress.currentQuestionIndex || 0);
        setTimeLeft(savedProgress.timeLeft || initialNumberOfQuestions * 60);
        setIsPaused(savedProgress.isPaused || false);
        setSelectedOption(savedProgress.selectedOption || null);
        setAnswers(savedProgress.answers || []);
        setNoAnswerQuestionIds(savedProgress.noAnswerQuestionIds || []);
      } else {
        // Reset if assessment_id does not match
        setNumberOfQuestions(query_count);
        setCurrentQuestionIndex(0);
        setTimeLeft(query_count * 60);
        setIsPaused(false);
        setSelectedOption(null);
        setAnswers([]);
        setNoAnswerQuestionIds([]);
      }
    }
  }, [query_count, assessment_id]);

  useEffect(() => {
    if (currentQuestionIndex !== null) {
      localStorage.setItem(
        "localQuizProgress",
        JSON.stringify({
          currentQuestionIndex,
          timeLeft,
          selectedOption,
          answers,
          isPaused,
          numberOfQuestions,
          noAnswerQuestionIds,
        }),
      );
    }
    if (timeLeft !== null && !isPaused && timeLeft > 0 && !submitLoading) {
      const timer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [
    currentQuestionIndex,
    timeLeft,
    isPaused,
    selectedOption,
    answers,
    numberOfQuestions,
    noAnswerQuestionIds,
    submitLoading,
  ]);

  useEffect(() => {
    if (timeLeft === 0) {
      handleTimeUp();
    }
  }, [timeLeft]);

  useEffect(() => {
    allowQuizAttempt();
    fetchQuestions(); // Fetch questions when component mounts
  }, [location.state]);

  const handleNextQuestion = () => {
    
    if (!selectedOption) {
      setShowAlert(true);
    } else {
      if (currentQuestionIndex === numberOfQuestions - 1) {
        // Add the last question's answer before submitting
        setAnswers([
          ...answers,
          {
            question: questions[currentQuestionIndex].question,
            selectedOption,
          },
        ]);
        if (selectedOption === "No") {
          setNoAnswerQuestionIds((prevIds) => {
            const updatedIds = [...prevIds, questions[currentQuestionIndex].id];
            localStorage.setItem(
              "noAnswerQuestionIds",
              JSON.stringify(updatedIds),
            );
            return updatedIds;
          });
        }
        // Directly submit the quiz
        submitQuiz();
      } else {
        proceedToNextQuestion();
      }
    }
  };

  const proceedToNextQuestion = () => {
    
    if (selectedOption === "No") {
      const currentQuestionId = questions[currentQuestionIndex].id;

      // Add current question ID to noAnswerQuestionIds
      setNoAnswerQuestionIds((prevIds) => {
        const updatedIds = [...prevIds, currentQuestionId];
        localStorage.setItem("noAnswerQuestionIds", JSON.stringify(updatedIds));
        return updatedIds;
      });
    }

    setAnswers([
      ...answers,
      { question: questions[currentQuestionIndex].question, selectedOption },
    ]);
    setSelectedOption(null);
    setShowAlert(false);

    if (currentQuestionIndex < numberOfQuestions - 1) {
      setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
    } else {
      submitQuiz();
    }
  };

  // Define the handleTimeUp function
  const handleTimeUp = () => {
    let updatedAnswers = [...answers];
    let updatedNoAnswerQuestionIds = [...noAnswerQuestionIds];

    if (selectedOption) {
      updatedAnswers.push({
        question: questions[currentQuestionIndex].question,
        selectedOption,
      });

      if (selectedOption === "No") {
        updatedNoAnswerQuestionIds.push(questions[currentQuestionIndex].id);
      }
    }

    // Update the state with the latest data
    setAnswers(updatedAnswers);
    setNoAnswerQuestionIds(updatedNoAnswerQuestionIds);

    // Optionally update local storage if needed
    localStorage.setItem(
      "noAnswerQuestionIds",
      JSON.stringify(updatedNoAnswerQuestionIds),
    );

    // Now call submitQuiz
    submitQuiz();
  };

  const submitQuiz = async () => {
    setSubmitLoading(true);
    try {
      const now = new Date();
      const currentDate = formatDate(now); // Get the formatted date
      const currentTime = formatStartTime(now); // Get the formatted time

      
      // Send noAnswerQuestionIds to backend
      const response = await axios.post(`${API_URL}/dashboard/loadlp`, {
        question_ids: noAnswerQuestionIds,
        assessment_id: assessment_id,
        user_id: user_id,
        date: currentDate,
        time: currentTime,
      });

      if (response) {
        resetQuizAttempt();
        navigate(`/dashboard/${user_id}`); // Navigate to dashboard
      } else {
        setSubmitLoading(false);
        alert("There was an error submitting your quiz.");
      }
    } catch (error) {
      setSubmitLoading(false);
      console.error("Error submitting quiz:", error);
      alert("There was an error submitting your quiz.");
    }
  };

  const handleAlertContinue = () => {
    proceedToNextQuestion();
  };

  const handlePause = () => {
    setIsPaused(!isPaused);
  };

  const handleStop = () => {
    alert("Quiz stopped!");
    resetQuizAttempt();
    navigate(`/dashboard/${user_id}`);
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  if (loading) {
    return (
      <Box fill align="center" justify="center">
        <Spinner size="large" />
        <Text>Loading Questions...</Text>
      </Box>
    );
  }

  return (
    <Box
      fill="vertical"
      justify="between"
      onContextMenu={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
      onSelect={(e) => e.preventDefault()}
    >
      <Box flex="grow">
        <HeaderNavigation />
        <QuizHeader
          currentQuestionIndex={currentQuestionIndex}
          numberOfQuestions={numberOfQuestions}
          isPaused={isPaused}
          timeLeft={timeLeft}
          handlePause={handlePause}
          handleStop={handleStop}
          formatTime={formatTime}
        />
        <QuizContent
          questions={questions}
          currentQuestionIndex={currentQuestionIndex}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          handleNextQuestion={handleNextQuestion}
          isPaused={isPaused}
          isLastQuestion={currentQuestionIndex === numberOfQuestions - 1}
        />
      </Box>
      <StickyFooter />

      {showAlert && (
        <Layer
          onEsc={() => setShowAlert(false)}
          onClickOutside={() => setShowAlert(false)}
        >
          <Box pad="medium" gap="small">
            <Text>You have not attempted this question.</Text>
            <Box direction="row" gap="medium" justify="center">
              <Button label="Continue" onClick={handleAlertContinue} />
              <Button label="Cancel" onClick={() => setShowAlert(false)} />
            </Box>
          </Box>
        </Layer>
      )}

      {/* {showSubmitPopup && (
        <Layer
          onEsc={() => setShowSubmitPopup(false)}
          onClickOutside={() => setShowSubmitPopup(false)}
        >
          <Box pad="medium" gap="small">
            <Text>Are you sure you want to submit the quiz?</Text>
            <Box direction="row" gap="medium" justify="center">
              <Button label="Submit" onClick={submitQuiz} primary />
              <Button
                label="Cancel"
                onClick={() => setShowSubmitPopup(false)}
              />
            </Box>
          </Box>
        </Layer>
      )} */}
    </Box>
  );
};
